import PropTypes from "prop-types";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import PendingActivationPage from "../../pages/PendingActivationPage";
import { rememberPostLoginPath } from "../../utils/postLoginPath";

/**
 * ProtectedRoute
 *
 * Purpose: Guards authenticated routes. A "Pending" user (self-registered
 * via SSO, awaiting admin approval) gets PendingActivationPage instead of
 * the app; there is no password/setup_required path to gate on anymore —
 * sign-in is SSO-only.
 * Used by: App route configuration.
 */
function ProtectedRoute({ children, requiredRole, requiredPermission }) {
  const { isAuthenticated, isPending, loading, isSuperAdmin, canUseExpenseImport, canViewExpenseRecord } =
    useAuth();
  const location = useLocation();
  const requiresSuperadmin = requiredRole === "superadmin";
  const PERMISSION_CHECKS = { expenseImport: canUseExpenseImport, expenseRecord: canViewExpenseRecord };

  if (loading) {
    return <div className="p-6 text-sm text-text-faint">Loading session...</div>;
  }

  if (!isAuthenticated) {
    rememberPostLoginPath(location);
    return <Navigate to="/login" replace />;
  }

  if (isPending) {
    return <PendingActivationPage />;
  }

  if (requiresSuperadmin && !isSuperAdmin()) {
    return <Navigate to="/dashboard" replace />;
  }

  if (requiredPermission && !PERMISSION_CHECKS[requiredPermission]?.()) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

ProtectedRoute.propTypes = {
  children: PropTypes.node.isRequired,
  requiredRole: PropTypes.string,
  requiredPermission: PropTypes.string,
};

ProtectedRoute.defaultProps = {
  requiredRole: null,
  requiredPermission: null,
};

export default ProtectedRoute;
